/* global Liferay */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Currency formatter
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

// Units formatter
const unitsFormatter = new Intl.NumberFormat('en-US');

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function App() {
  const [region, setRegion] = useState('ALL');
  const [year, setYear] = useState('ALL');
  const [model, setModel] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [monthlyUnits, setMonthlyUnits] = useState(new Array(12).fill(0));

  // Fetch sales data from Liferay Object API
  const fetchSalesData = useCallback(() => {
    setLoading(true);

    // Build OData dynamic filters
    const filters = [];
    if (region !== 'ALL') {
      filters.push(`region eq '${region}'`);
    }
    if (year !== 'ALL') {
      filters.push(`year eq ${year}`);
    }
    if (model !== 'ALL') {
      filters.push(`motorbikeModel eq '${model}'`);
    }

    let filterQuery = '';
    if (filters.length > 0) {
      filterQuery = `&filter=${encodeURIComponent(filters.join(' and '))}`;
    }

    const url = `/o/c/motorbikesalesrecords?pageSize=1000${filterQuery}`;
    console.log("Fetching Solara Sales data in React from:", url);

    // Check if Liferay.Util.fetch is available (Liferay environment)
    if (typeof Liferay !== 'undefined' && Liferay.Util && Liferay.Util.fetch) {
      Liferay.Util.fetch(url)
        .then(response => {
          if (!response.ok) throw new Error("HTTP error " + response.status);
          return response.json();
        })
        .then(data => {
          const items = data.items || [];
          setSalesData(items);
        })
        .catch(err => {
          console.error("Failed to fetch motorbike sales data in React:", err);
          setSalesData([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Non-Liferay environment: Fallback with Mock/empty data for safe development
      console.warn("Liferay is not defined. Using mock local data for development.");
      setTimeout(() => {
        // Generate mock items for testing
        const mockItems = [
          { region: 'EMEA', year: 2025, motorbikeModel: 'Solara TrailMaster 500', unitsSold: 12, revenue: 120000, month: 1 },
          { region: 'EMEA', year: 2025, motorbikeModel: 'Solara TrailMaster 500', unitsSold: 15, revenue: 150000, month: 2 },
          { region: 'NA', year: 2026, motorbikeModel: 'Solara Urban Cruiser', unitsSold: 8, revenue: 96000, month: 3 },
          { region: 'LATAM', year: 2026, motorbikeModel: 'Solara MetroVolt EV', unitsSold: 20, revenue: 180000, month: 6 },
          { region: 'APAC', year: 2026, motorbikeModel: 'Solara Cafe Racer Classic', unitsSold: 5, revenue: 45000, month: 12 },
        ];
        
        // Simple client-side filtering on mock data to make local dev feel real
        const filtered = mockItems.filter(item => {
          if (region !== 'ALL' && item.region !== region) return false;
          if (year !== 'ALL' && item.year.toString() !== year) return false;
          if (model !== 'ALL' && item.motorbikeModel !== model) return false;
          return true;
        });

        setSalesData(filtered);
        setLoading(false);
      }, 400);
    }
  }, [region, year, model]);

  // Re-fetch when any filter changes
  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  // Recalculate KPIs and Monthly Chart values whenever salesData changes
  useEffect(() => {
    let unitsAgg = 0;
    let revenueAgg = 0;
    const monthlyAgg = new Array(12).fill(0);

    salesData.forEach(record => {
      unitsAgg += record.unitsSold || 0;
      revenueAgg += record.revenue || 0;

      const monthIndex = (record.month || 1) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        monthlyAgg[monthIndex] += record.unitsSold || 0;
      }
    });

    setTotalUnits(unitsAgg);
    setTotalRevenue(revenueAgg);
    setMonthlyUnits(monthlyAgg);
  }, [salesData]);

  // ChartJS Data setup with custom gradient
  const chartData = {
    labels: MONTH_LABELS,
    datasets: [{
      label: 'Motorbikes Sold (Units)',
      data: monthlyUnits,
      borderColor: darkMode ? 'rgba(56, 189, 248, 1)' : 'rgba(11, 110, 255, 1)', // SKY-400 under dark mode, standard blue under light mode
      backgroundColor: (context) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) return null;
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        if (darkMode) {
          gradient.addColorStop(0, 'rgba(56, 189, 248, 0.45)');
          gradient.addColorStop(1, 'rgba(56, 189, 248, 0.01)');
        } else {
          gradient.addColorStop(0, 'rgba(11, 110, 255, 0.35)');
          gradient.addColorStop(1, 'rgba(11, 110, 255, 0.01)');
        }
        return gradient;
      },
      borderWidth: 3,
      fill: true,
      tension: 0.38,
      pointBackgroundColor: darkMode ? 'rgba(56, 189, 248, 1)' : 'rgba(11, 110, 255, 1)',
      pointBorderColor: darkMode ? '#0f172a' : '#fff',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 7
    }]
  };

  // ChartJS Options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: darkMode ? '#0f172a' : '#1e293b',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 13 },
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return ` Units Sold: ${unitsFormatter.format(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: { size: 11 },
          color: darkMode ? '#94a3b8' : '#64748b',
          callback: function(value) {
            return unitsFormatter.format(value);
          }
        },
        suggestedMin: 0,
        suggestedMax: 20
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: { size: 11, weight: '600' },
          color: darkMode ? '#94a3b8' : '#64748b'
        }
      }
    }
  };

  return (
    <div className={`solara-sales-chart-widget container-fluid p-4 ${darkMode ? 'dark-mode' : ''}`}>
      <div className={`card border-0 shadow-sm rounded p-4 ${darkMode ? 'bg-dark-card text-white' : 'bg-white'}`}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
          <div>
            <h3 className={`font-weight-bold mb-1 ${darkMode ? 'text-white' : 'text-dark'}`}>
              Solara Motorbike Sales Evolution
            </h3>
            <p className={`small mb-0 ${darkMode ? 'text-muted-dark' : 'text-muted'}`}>
              Real-time synchronized sales reporting and trend analytics (React Client Extension)
            </p>
          </div>
          {/* Controls (Toggle + Sync) */}
          <div className="d-flex align-items-center flex-wrap mt-3 mt-md-0">
            {/* Custom Premium Toggle Switch */}
            <div className="theme-switch-container mr-4 d-flex align-items-center">
              <span className={`toggle-label mr-2 mb-0 small text-uppercase font-weight-bold ${darkMode ? 'text-info' : 'text-muted'}`}>
                {darkMode ? 'Dark Theme' : 'Light Theme'}
              </span>
              <div className="theme-toggle-wrapper">
                <input
                  type="checkbox"
                  className="theme-toggle-checkbox"
                  id="darkModeToggle"
                  checked={darkMode}
                  onChange={() => setDarkMode(!darkMode)}
                />
                <label className="theme-toggle-slider" htmlFor="darkModeToggle"></label>
              </div>
            </div>

            {/* Loading Indicator */}
            {loading && (
              <div className="sales-chart-loading text-primary d-flex align-items-center">
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                <small className="font-weight-bold">Syncing data...</small>
              </div>
            )}
          </div>
        </div>

        {/* Filter Dropdowns Row */}
        <div className={`row rounded p-3 mb-4 mx-0 align-items-end ${darkMode ? 'bg-dark-filters border border-dark' : 'bg-light'}`}>
          {/* Region Filter */}
          <div className="col-md-4 col-sm-12 mb-3 mb-md-0 pl-md-0 pr-md-2">
            <label htmlFor="filterRegion" className={`text-uppercase small font-weight-bold mb-2 d-block ${darkMode ? 'text-muted-dark' : 'text-muted'}`}>Region / Territory</label>
            <select
              id="filterRegion"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className={`form-control form-select custom-select border ${darkMode ? 'bg-dark-input text-white border-secondary' : 'bg-white'}`}
            >
              <option value="ALL">All Territories</option>
              <option value="EMEA">EMEA (Europe, Middle East, Africa)</option>
              <option value="NA">NA (North America)</option>
              <option value="LATAM">LATAM (Latin America)</option>
              <option value="APAC">APAC (Asia-Pacific)</option>
            </select>
          </div>

          {/* Year Filter */}
          <div className="col-md-3 col-sm-12 mb-3 mb-md-0 px-md-2">
            <label htmlFor="filterYear" className={`text-uppercase small font-weight-bold mb-2 d-block ${darkMode ? 'text-muted-dark' : 'text-muted'}`}>Reporting Year</label>
            <select
              id="filterYear"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={`form-control form-select custom-select border ${darkMode ? 'bg-dark-input text-white border-secondary' : 'bg-white'}`}
            >
              <option value="ALL">All Years (2025 - 2026)</option>
              <option value="2025">2025 (Historical)</option>
              <option value="2026">2026 (YTD)</option>
            </select>
          </div>

          {/* Model Filter */}
          <div className="col-md-5 col-sm-12 mb-0 pr-md-0 pl-md-2">
            <label htmlFor="filterModel" className={`text-uppercase small font-weight-bold mb-2 d-block ${darkMode ? 'text-muted-dark' : 'text-muted'}`}>Motorbike Model</label>
            <select
              id="filterModel"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className={`form-control form-select custom-select border ${darkMode ? 'bg-dark-input text-white border-secondary' : 'bg-white'}`}
            >
              <option value="ALL">All Models (Total Portfolio)</option>
              <option value="Solara TrailMaster 500">Solara TrailMaster 500 (Adventure)</option>
              <option value="Solara Urban Cruiser">Solara Urban Cruiser (Touring)</option>
              <option value="Solara MetroVolt EV">Solara MetroVolt EV (Electric)</option>
              <option value="Solara Cafe Racer Classic">Solara Cafe Racer Classic (Heritage)</option>
            </select>
          </div>
        </div>

        {/* Summary KPIs Row */}
        <div className="row mb-4">
          {/* Total Units KPI */}
          <div className="col-md-6 col-sm-12 mb-3 mb-md-0">
            <div className={`border rounded p-3 d-flex align-items-center ${darkMode ? 'bg-dark-kpi text-white' : 'bg-white'}`}>
              <div className={`kpi-icon rounded-circle d-flex justify-content-center align-items-center mr-3 ${darkMode ? 'bg-dark-kpi-units text-info' : 'bg-primary-light text-primary'}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="1" y="3" width="15" height="13" rx="2" ry="2"></rect>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                </svg>
              </div>
              <div>
                <small className={`text-uppercase d-block mb-1 ${darkMode ? 'text-muted-dark' : 'text-muted'}`}>Total Units Sold</small>
                <h3 className="font-weight-bold mb-0">
                  {unitsFormatter.format(totalUnits)}
                </h3>
              </div>
            </div>
          </div>

          {/* Total Revenue KPI */}
          <div className="col-md-6 col-sm-12">
            <div className={`border rounded p-3 d-flex align-items-center ${darkMode ? 'bg-dark-kpi text-white' : 'bg-white'}`}>
              <div className={`kpi-icon rounded-circle d-flex justify-content-center align-items-center mr-3 ${darkMode ? 'bg-dark-kpi-rev text-success' : 'bg-success-light text-success'}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div>
                <small className={`text-uppercase d-block mb-1 ${darkMode ? 'text-muted-dark' : 'text-muted'}`}>Gross Sales Revenue</small>
                <h3 className="font-weight-bold mb-0">
                  {currencyFormatter.format(totalRevenue)}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Canvas Container */}
        <div className="position-relative w-100 mb-2" style={{ height: '350px' }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}

export default App;
